// 11 Sep 1906
//
// REFACTORED TO USE CENTRALIZED HOOK
// All complex vitals logic moved to useVitalsForm hook for easier maintenance
//
import dayjs from 'dayjs'
import { VitalsItemContainer } from './VitalsListingPageStyles'
import { useVitalsForm } from 'src/reducers/vitalsFormSettings/hooks/useVitalsForm'

// Legacy constants - replaced by dynamic data

// Utility functions
export function convertToFahrenheitFromCelsius(celsius) {
  return parseFloat(((celsius * 9) / 5 + 32).toFixed(1))
}

export function convertToCelsiusFromFahrenheit(fahrenheit) {
  return (((fahrenheit - 32) * 5) / 9).toFixed(1)
}

export function isVitalObjectValid(vitalItem, vitalsFields = []) {
  if (!vitalItem) return false

  // Check any of the vital fields have values
  return vitalsFields.some((field) => {
    if (field.ui?.groupId === 'bp') {
      return vitalItem[field.id]
    }
    return vitalItem[field.id]
  })
}

// Legacy helper functions - now handled by vitalsUtils and dynamic data

export const VitalShowingComponent = ({ vitalItem, useEmStyles = false }) => {
  // Use centralized vitals hook (read-only mode)
  const { vitalsFields, getDisplayValue, getDefaultUnit } = useVitalsForm({
    existingVitalData: vitalItem,
    autoFetch: true,
  })

  if (!vitalItem || !isVitalObjectValid(vitalItem, vitalsFields)) {
    return null
  }

  const renderVitalField = (field) => {
    const value = vitalItem?.[field.id]
    if (value === null || value === undefined) return null

    const defaultUnit = getDefaultUnit(field)
    const displayValue = getDisplayValue(field)
    const unitText = defaultUnit?.label || ''

    // Handle grouped fields (like blood pressure)
    if (field.ui?.groupId === 'bp' && field.ui?.groupHeader) {
      const otherBpField = vitalsFields.find((f) => f.ui?.groupId === 'bp' && f.id !== field.id)

      if (otherBpField) {
        const otherDisplayValue = getDisplayValue(otherBpField)

        // Round blood pressure values (whole numbers)
        const roundedDisplayValue =
          displayValue && !isNaN(displayValue)
            ? parseFloat(displayValue).toFixed(0)
            : displayValue || '-'
        const roundedOtherValue =
          otherDisplayValue && !isNaN(otherDisplayValue)
            ? parseFloat(otherDisplayValue).toFixed(0)
            : otherDisplayValue || '-'

        return (
          <div key={field.id}>
            {field.ui.groupLabel || field.label}:{' '}
            <b>
              {roundedDisplayValue}/{roundedOtherValue} {unitText}
            </b>
          </div>
        )
      }
    } else if (field.ui?.groupId === 'bp' && !field.ui?.groupHeader) {
      // Skip rendering the second BP field as it's handled above
      return null
    }

    // Handle temperature with dual unit display
    if (field.id === 'tempInCelsius' && displayValue) {
      // Round temperature to 1 decimal place
      const roundedCelsius = parseFloat(displayValue).toFixed(1)
      const fahrenheit = convertToFahrenheitFromCelsius(displayValue)
      return (
        <div key={field.id}>
          {field.label}:{' '}
          <b>
            {roundedCelsius}°C / {fahrenheit}°F
          </b>
        </div>
      )
    }

    // Format all numeric values with proper rounding
    let formattedValue = displayValue
    if (displayValue !== null && displayValue !== undefined && !isNaN(displayValue)) {
      // Get decimals from unit configuration, with sensible defaults by field type
      let decimals = defaultUnit?.decimals

      if (decimals === undefined) {
        // Apply default decimals based on field type/id patterns
        if (field.type === 'calculated') {
          decimals = 2 // BMI, ratios, etc.
        } else if (field.id.includes('temp') || field.id.includes('Temp')) {
          decimals = 1 // Temperature
        } else if (field.id.includes('bp') || field.id.includes('Bp') || field.id.includes('BP')) {
          decimals = 0 // Blood pressure - whole numbers
        } else if (
          field.id.includes('bpm') ||
          field.id.includes('Rate') ||
          field.id.includes('rate')
        ) {
          decimals = 0 // Heart rate, respiratory rate - whole numbers
        } else if (
          field.id.includes('sp02') ||
          field.id.includes('SpO2') ||
          field.id.includes('spo2')
        ) {
          decimals = 0 // Oxygen saturation - whole numbers
        } else if (field.id.includes('weight') || field.id.includes('Weight')) {
          decimals = 1 // Weight
        } else if (field.id.includes('height') || field.id.includes('Height')) {
          decimals = 1 // Height
        } else {
          decimals = 2 // Default for other numeric fields
        }
      }

      formattedValue = parseFloat(displayValue).toFixed(decimals)
    }

    return (
      <div key={field.id}>
        {field.label}:{' '}
        <b>
          {formattedValue} {unitText}
        </b>
      </div>
    )
  }

  return (
    <VitalsItemContainer style={useEmStyles ? { fontSize: '0.75em' } : {}}>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ background: '#b838ff3d' }}
      >
        <div
          style={{
            fontSize: useEmStyles ? '0.65em' : '12px',
            textAlign: 'right',
            marginBottom: useEmStyles ? '1em' : '16px',
          }}
        >
          Recorded By: {vitalItem?.recordedByName || '--'}
        </div>
        <div
          style={{
            fontSize: useEmStyles ? '0.65em' : '12px',
            textAlign: 'right',
            marginBottom: useEmStyles ? '1em' : '16px',
          }}
        >
          Recorded: {dayjs.utc(vitalItem?.modifiedTime)?.local()?.format('DD-MM-YYYY h:mm A')}
        </div>
      </div>
      <div className="d-flex flex-column gap-2 px-2 pb-2">
        {vitalsFields.map(renderVitalField).filter(Boolean)}
      </div>
    </VitalsItemContainer>
  )
}
